import { Button, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import axios from 'axios';
import useAxios from 'axios-hooks';
import cuid from 'cuid';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Alert, Box, Col, DataGrid, DatePicker, FormControl, IconButton, Paper, Radio, RadioGroup, Row, Select, TextField } from 'components/AORedesign';
import { GET_CL_WIP_PRODUCT_LINES_URL, GET_COMMERCIAL_LINES, GET_TRANSACTIONS_URL } from '../../constants/api';
import { dataGridDateFormatter } from '../../helpers';
import { AgentLookupModal } from '../AgentLookupModal';
import { redirectUrlFunction } from '../CommonUtilityServices/RedirectService';
import { API_CLS_WIP_BASE_URL } from './CommercialLinesServices.api';

const CLSWip = ({ navSource }) => {
  const [isWipRedirectErrorVisible, setIsWipRedirectErrorVisible] = useState(false);
  const [wipRedirectError, setWipRedirectError] = useState(false);

  // Num days for date range for entry date.  Default is 7.  Set to 30 if coming from Unsold Quotes Card.
  const entryDateRangeDays = navSource == 'unsoldQuotesCard' ? 30 : 7;

  const renderCell = (params) => {
    const handleClickOpenPolicy = () => {
      const selectedPolicyObject = params.row;
      let request_body = {
        Area: 'Commercial Lines',
        DetailedContext: [
          {
            name: 'businessType',
            value: 'CL',
          },
          {
            name: 'quoteNumber',
            value: params.value,
          },
        ],
      };

      request_body.TypeOfRedirect = 'Wip CL Resume Quote';
      if (selectedPolicyObject.tranId !== undefined) {
        request_body.DetailedContext.push(
          {
            name: 'id',
            value: selectedPolicyObject.tranId,
          },
          {
            name: 'system',
            value: selectedPolicyObject.system,
          },
          {
            name: 'lob',
            value: selectedPolicyObject.lob,
          },
          {
            name: 'status',
            value: selectedPolicyObject.status,
          },
          {
            name: 'tranType',
            value: selectedPolicyObject.tranType,
          },
          {
            name: 'submitInd',
            value: selectedPolicyObject.submitInd,
          },
          {
            name: 'processStatus',
            value: selectedPolicyObject.processStatus,
          },
          {
            name: 'accessMsg',
            value: selectedPolicyObject.accessMsg,
          },
          {
            name: 'flgRes',
            value: selectedPolicyObject.flgRes,
          },
          {
            name: 'risk',
            value: selectedPolicyObject.risk,
          },
          {
            name: 'agencyNumber',
            value: selectedPolicyObject.agencyNumber,
          },
        );
      }
      return redirectUrlFunction(request_body, 'POST', null, setWipRedirectError, setIsWipRedirectErrorVisible);
    };
    return !_.isNil(params.row) && params.row.tranType !== 'Quote Decline' ? (
      <Button
        aria-label="policy/qt"
        onClick={() => {
          handleClickOpenPolicy();
        }}
        color="primary">
        {params.value}
      </Button>
    ) : (
      <Typography>{params.value} </Typography>
    );
  };
  const clWipSearchResultsColumnsData = [
    {
      field: 'quotePolNum',
      headerName: 'Policy/QT/Ref #',
      editable: false,
      flex: 1,
      renderCell,
    },
    {
      field: 'insName',
      headerName: 'Named Insured/Description',
      flex: 1,
      editable: false,
    },
    {
      field: 'lob',
      headerName: 'LOB',
      type: 'number',
      flex: 1,
      editable: false,
    },
    {
      field: 'tranType',
      headerName: 'Transaction',
      type: 'string',
      flex: 1,
      editable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'number',
      flex: 1,
      editable: false,
    },
    {
      field: 'effDate',
      headerName: 'Effective Date',
      type: 'date',
      flex: 1,
      editable: false,
      valueFormatter: dataGridDateFormatter,
    },
    {
      field: 'entDate',
      headerName: 'Entered Date',
      type: 'date',
      flex: 1,
      editable: false,
      valueFormatter: dataGridDateFormatter,
    },
    {
      field: 'userID',
      headerName: 'User ID',
      type: 'number',
      flex: 1,
      editable: false,
    },
    {
      field: 'location',
      headerName: 'Agent Code',
      type: 'number',
      flex: 1,
      editable: false,
    },
  ];

  const mapTransactionMenuItems = (items) => {
    // Check if items is an array before calling map
    if (!Array.isArray(items)) {
      console.warn('mapTransactionMenuItems received non-array data:', items);
      return []; // Return empty array as fallback
    }

    const mappedItems = items.map((item) => {
      const newItem = {
        ...item,
        value: item.tranId === '0' ? 'ALL' : item.tranId,
        label: item.tranDesc,
      };
      delete newItem.tranId;
      delete newItem.tranDesc;
      return newItem;
    });

    return mappedItems;
  };

  const [clWipTransactionMenuItems, setClWipTransactionMenuItems] = useState([]);
  const getTransactionsUrl = `${GET_TRANSACTIONS_URL}${GET_COMMERCIAL_LINES}`;
  const [{ data: response, loading }] = useAxios(getTransactionsUrl);
  useEffect(() => {
    if (!loading && response) {
      setClWipTransactionMenuItems(mapTransactionMenuItems(response));
    }
  }, [loading, response]);

  // CL Wip State variables
  const [clWipSearchResultsColumns] = useState(clWipSearchResultsColumnsData);
  const [fetchingData, setFetchingData] = useState(true);
  const [clWipSearch, setClWipSearch] = useState();
  const [clWipSearchLoading, setClWipSearchLoading] = useState(true);
  const [clWipSearchError, setClWipSearchError] = useState(null);
  const [clWipAgentCodeValue, setClWipAgentCodeValue] = useState('');

  const [clWipSearchType, setClWipSearchType] = useState('enterDate');
  const [clWipSearchTypeValue, setClWipSearchTypeValue] = useState('');
  const [clWipEntryStartDate, setClWipEntryStartDate] = useState(moment().subtract(entryDateRangeDays, 'days').format('MM-DD-YYYY'));
  const [clWipEntryEndDate, setClWipEntryEndDate] = useState(moment().format('MM-DD-YYYY'));

  const [clWipEffStartDate, setClWipEffStartDate] = useState(moment().format('MM-DD-YYYY'));
  const [clWipEffEndDate, setClWipEffEndDate] = useState(moment().add(7, 'days').format('MM-DD-YYYY'));
  const [isAgentLookupModalOpen, setAgentLookupModalOpen] = useState(false);

  const clWipProductLinesDefaultOptionValue = 'All';
  const clWipProductLinesDefaultOptionCode = '0';
  const [clWipLob, setClWipLob] = useState(clWipProductLinesDefaultOptionCode);
  const [clWipLobIsDisabled, setClWipLobIsDisabled] = useState(false);
  const [clWipProductLines, setClWipProductLines] = useState([]);
  const productLinesUrl = `${GET_CL_WIP_PRODUCT_LINES_URL}`;
  const [{ data: productLinesData, loading: productLinesLoading, error: productLinesError }] = useAxios({
    url: `${productLinesUrl}`,
  });

  console.error(productLinesError);

  const [clWipTransaction, setClWipTransaction] = useState('ALL');
  const [clWipTransactionIsDisabled, setClWipTransactionIsDisabled] = useState(false);

  const handleChangeClWipSearchType = (event) => {
    const lob = event.target.value;
    setClWipLobIsDisabled(false);
    setClWipSearchTypeValue('');
    setClWipLob(clWipProductLinesDefaultOptionCode);
    setClWipTransaction('ALL');
    setClWipAgentCodeValue('');
    setClWipSearchType(lob);
  };
  const handleChangeClWipSearchTypeValue = (event) => {
    setClWipSearchTypeValue(event.target.value);
  };
  const handleChangeClWipEntryStartDate = (date) => {
    setClWipEntryStartDate(moment(date).format('MM-DD-YYYY'));
  };
  const handleChangeClWipEntryEndDate = (date) => {
    setClWipEntryEndDate(moment(date).format('MM-DD-YYYY'));
  };
  const handleChangeClWipEffStartDate = (date) => {
    setClWipEffStartDate(moment(date).format('MM-DD-YYYY'));
  };
  const handleChangeClWipEffEndDate = (date) => {
    setClWipEffEndDate(moment(date).format('MM-DD-YYYY'));
  };
  const handleChangeClWipLob = (event) => {
    setClWipLob(event.target.value);
  };
  const handleChangeClWipTransaction = (event) => {
    setClWipTransaction(event.target.value);
  };
  const handleChangeClWipAgentCodeValue = (event) => {
    setClWipAgentCodeValue(event.target.value);
  };
  const handleAgentLookupModalOpen = () => {
    setAgentLookupModalOpen(true);
  };

  const buildClWipSearchEndpoint = () => {
    let endpoint;

    // Agent Filter
    const agentCode = clWipAgentCodeValue ? `&agency=${clWipAgentCodeValue}` : '';

    // LOB Filter
    const lob = `&lob=${clWipLob === '0' ? '10' : clWipLob}`;

    // Transaction Filter
    const mappedTransaction = clWipTransaction === 'ALL' ? '0' : clWipTransaction; // if transaction is ALL then send 0
    const transaction = mappedTransaction ? `tran=${mappedTransaction}` : '';

    switch (clWipSearchType) {
      case 'insuredName': {
        endpoint = `&${transaction}&searchCriteria=insuredName&searchValue=${clWipSearchTypeValue}${agentCode}${lob}`;
        break;
      }
      case 'quote': {
        endpoint = `&${transaction}&searchCriteria=quote&searchValue=${clWipSearchTypeValue}${agentCode}${lob}`;
        break;
      }
      case 'policyNumber': {
        endpoint = `&${transaction}&searchCriteria=policyNumber&searchValue=${clWipSearchTypeValue}${agentCode}${lob}`;
        break;
      }
      case 'effectiveDate': {
        endpoint = `&${transaction}&searchCriteria=effectiveDate&startDate=${clWipEffStartDate}&endDate=${clWipEffEndDate}${lob}${agentCode}`;
        break;
      }
      default: {
        endpoint = `&${transaction}&searchCriteria=enterDate&startDate=${clWipEntryStartDate}&endDate=${clWipEntryEndDate}${lob}${agentCode}`;
        break;
      }
    }
    return endpoint;
  };

  const submitClWip = () => {
    setFetchingData(true);
    setClWipSearch();
    setClWipSearchError(null);
    setClWipSearchLoading(true);
    const CL_WIP_ENDPOINT = buildClWipSearchEndpoint();

    /* Check if endpoint contains restrictedLobValues that should not make the API call
    1: Businessowners/Contr.
    2: Commercial Auto
    5: Commercial Umbrella
    6: Workers Compensation
    */
    const restrictedLobValues = [1, 2, 5, 6];
    const hasRestrictedLob = restrictedLobValues.includes(clWipLob);

    if (hasRestrictedLob) {
      setClWipSearchLoading(false);
      setFetchingData(false);
      setClWipSearch([]);
      return;
    }

    axios(`${API_CLS_WIP_BASE_URL}${CL_WIP_ENDPOINT}`)
      .then((results) => {
        setClWipSearchLoading(false);
        const WorkInProgress = results.data.WorkInProgress;
        const resWithId = WorkInProgress.map((el) => {
          return { id: cuid(), ...el };
        });
        setFetchingData(false);
        setClWipSearch(resWithId);
      })
      .catch((error) => {
        console.error(error);
        setClWipSearchError(error);
      });
  };

  const resetClWip = () => {
    setClWipEntryStartDate(moment().subtract(entryDateRangeDays, 'days').format('MM-DD-YYYY'));
    setClWipEntryEndDate(moment().format('MM-DD-YYYY'));
    setClWipEffStartDate(moment().format('MM-DD-YYYY'));
    setClWipEffEndDate(moment().add(7, 'days').format('MM-DD-YYYY'));
    setClWipSearchType('enterDate');
    setClWipAgentCodeValue('');
    setClWipLob(clWipProductLinesDefaultOptionCode);
    setClWipTransaction('ALL');
    setClWipLobIsDisabled(false);
    setClWipTransactionIsDisabled(false);
    setClWipSearchTypeValue('');
  };

  useEffect(() => {
    submitClWip();
  }, []);

  useEffect(() => {
    if (!productLinesLoading) {
      const productLinesArray = Array.isArray(productLinesData) ? productLinesData : [];

      const productLinesMenuItems = productLinesArray.map((el) => {
        return {
          value: el.numericCodeForWIP,
          label: el.productLineName,
        };
      });

      const productLineMenuItemsWithDefaultOption = [
        {
          value: clWipProductLinesDefaultOptionCode,
          label: clWipProductLinesDefaultOptionValue,
        },
        ...productLinesMenuItems,
      ];

      setClWipProductLines(productLineMenuItemsWithDefaultOption);
    }
  }, [productLinesLoading, productLinesData]);

  return (
    <>
      <Typography variant="subtitle1">Work In Progress</Typography>

      <Box id={'ClsWipBoxPaperTypgraphySearchCriteria'} mb={2}>
        <Paper id={'ClsWipPaperTypographySearchCriteria'} variant={'outlined'} square>
          <Box id={'ClsWipBoxTypgraphySearchCriteria'}>
            <Typography id={'ClsWipTypgraphySearchCriteria'} variant={'subtitle1'}>
              Search Criteria
            </Typography>
          </Box>

          <Box id={'RadioButtonSection'}>
            <FormControl component={'fieldset'}>
              <RadioGroup aria-label={'cl-wip-searchtype'} name={'cl-wip-searchtype'} row value={clWipSearchType} onChange={handleChangeClWipSearchType}>
                <Radio id={'cls-wip-insured-name-radio'} value={'insuredName'} label={'Insured Name'} />
                <Radio id={'cls-wip-quote-app-radio'} value={'quote'} label={'Quote/ App'} />
                <Radio id={'cls-wip-policy-number-radio'} value={'policyNumber'} label={'Policy Number'} />
                <Radio id={'cls-wip-entry-date-radio'} value={'enterDate'} label={'Entry Date'} />
                <Radio id={'cls-wip-effective-date-radio'} value={'effectiveDate'} label={'Effective Date'} />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box id={'ClsWipBoxSearchTypeSpacer'} my={4} />

          {clWipSearchType === 'insuredName' && (
            <Box id={'ClsWipBoxTypgraphyInsuredName'}>
              <Typography id={'ClsWipTypographyInsuredName'} variant={'subtitle1'}>
                Insured Name
              </Typography>
              <Box id={'ClWipTypgraphyInsuredNameSpacer'} mb={2}>
                <TextField
                  id={'ClsWipTextFieldSearchInput'}
                  label={'Enter Search Input'}
                  value={clWipSearchTypeValue}
                  onChange={handleChangeClWipSearchTypeValue}
                />
              </Box>
            </Box>
          )}

          {clWipSearchType === 'quote' && (
            <Box id={'ClsWipBoxQuoteApp'}>
              <Typography id={'ClsWipTypographyQuoteApp'} variant={'subtitle1'}>
                Quote/ App
              </Typography>
              <Box id={'ClsWipQuoteTextFieldSpacer'} mb={2}>
                <TextField
                  id={'ClsWipTextFieldSearchInput'}
                  label={'Enter Search Input'}
                  value={clWipSearchTypeValue}
                  onChange={handleChangeClWipSearchTypeValue}></TextField>
              </Box>
            </Box>
          )}

          {clWipSearchType === 'policyNumber' && (
            <Box id={'ClsWipBoxTypographyPolicyNumber'}>
              <Typography id={'ClsWipTypographyPolicyNumber'} variant={'subtitle1'}>
                Policy Number
              </Typography>
              <Box id={'ClsWip:TypgraphySpacer'} mb={2}>
                <TextField
                  id={'ClsWipTextFieldSearchInput'}
                  label={'Enter Search Input'}
                  value={clWipSearchTypeValue}
                  onChange={handleChangeClWipSearchTypeValue}></TextField>
              </Box>
            </Box>
          )}

          {clWipSearchType === 'enterDate' && (
            <>
              <Box id={'ClsWipBoxTypographyEntryDate'}>
                <Typography id={'ClsWipTypographyEntryDate'} variant={'subtitle1'}>
                  Entry Date
                </Typography>
                <Box id={'ClsWipTypographyEntryDateSpacer'} mb={2} />
              </Box>
              <Row id={'ClsWipRowDatePickers'} spacing={2}>
                <Col id={'ClsWipColDatePickerStartDate'}>
                  <DatePicker id={'ClsWipDatePickerStartDate'} label={'Start Date'} value={clWipEntryStartDate} onChange={handleChangeClWipEntryStartDate} />
                </Col>
                <Col id={'ClsWipColDatePickers'}>
                  <DatePicker id={'ClsWipDatePickerEndDate'} label={'End Date'} value={clWipEntryEndDate} onChange={handleChangeClWipEntryEndDate} />
                </Col>
              </Row>
            </>
          )}

          {clWipSearchType === 'effectiveDate' && (
            <>
              <Box id={'ClsWipBoxTypographyEffectiveDate'}>
                <Typography id={'ClsWipTypographyEffectiveDate'} variant={'subtitle1'}>
                  Effective Date
                </Typography>
                <Box id={'ClsWipTypographyEffectiveDateSpacer'} mb={2} />
              </Box>
              <Row id={'ClsWipDatePickersEffectiveDate'} spacing={2}>
                <Col id={'ClsWipColEffectiveDatePickerStartDate'}>
                  <DatePicker id={'ClsWipEffectiveDatePicker'} label={'Start Date'} value={clWipEffStartDate} onChange={handleChangeClWipEffStartDate} />
                </Col>
                <Col id={'ClsWipColEffectiveDatePicker'}>
                  <DatePicker id={'ClsWipEffectiveDatePickerEndDate'} label={'End Date'} value={clWipEffEndDate} onChange={handleChangeClWipEffEndDate} />
                </Col>
              </Row>
            </>
          )}
        </Paper>
      </Box>
      <Box id={'ClsWipBoxPaperContainer'} mb={2}>
        <Paper id={'ClsWipPaperSearchFilters'} variant={'outlined'} square>
          <Box id={'ClsWipBoxTypographySearchFilters'}>
            <Typography id={'ClsWipTypographySearchFilters'} variant={'subtitle1'}>
              Search Filters
            </Typography>
            <Box id={'ClsWipTypographySearchFiltersSpacer'} mb={2} />
          </Box>
          <Row id={'ClsWipRowLobSelect'} spacing={2}>
            <Col id={'ClsWipColSelect'}>
              <Select
                id={'line-of-business'}
                labelId={'line-of-business-label'}
                label={'Line of Business'}
                value={clWipLob}
                menuItems={clWipProductLines}
                disabled={clWipLobIsDisabled}
                onChange={handleChangeClWipLob}
              />
            </Col>
            <Col id={'ClsWipColSelect'}>
              <Select
                id={'transactions'}
                labelId={'transactions-label'}
                label={'Transactions'}
                value={clWipTransaction}
                menuItems={clWipTransactionMenuItems}
                disabled={clWipTransactionIsDisabled}
                onChange={handleChangeClWipTransaction}
              />
            </Col>
            <Col id={'ClsWipColTextFieldAgentCode'}>
              <Row alignItems={'center'}>
                <TextField
                  id={'ClsWipTextFieldAgentCode'}
                  label={'Agent Code'}
                  value={clWipAgentCodeValue}
                  onChange={handleChangeClWipAgentCodeValue}></TextField>
                <Box ml={0.25}>
                  <IconButton id={'cl-wip-agent-lookup-button'} onClick={handleAgentLookupModalOpen} color="textSecondary" size="medium">
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Row>
            </Col>
          </Row>
        </Paper>
      </Box>
      <Row id={'ClsWipRowButtonSearch'} spacing={2}>
        <Col id={'ClsWipColButtonSearch'}>
          <Button variant={'contained'} color={'primary'} loading={fetchingData} onClick={submitClWip}>
            Search
          </Button>
        </Col>
        <Col id={'ClsWipColButtonReset'}>
          <Button id={'ClsWipButtonReset'} variant={'contained'} onClick={resetClWip}>
            Reset
          </Button>
        </Col>
      </Row>
      <Box id={'ClsWipBoxAODataGrid'}>
        <Box id={'ClsWipAODataGridSpacer'} my={2}>
          {isWipRedirectErrorVisible && <Alert id={'clWipRedirectAlertComponent'} severity={'error'} message={wipRedirectError} />}

          <DataGrid
            id={'ClsWipAODataGrid'}
            autoHeight
            pageSize={50}
            loading={clWipSearchLoading}
            error={clWipSearchError}
            rows={clWipSearch}
            columns={clWipSearchResultsColumns}
          />
        </Box>
      </Box>

      <AgentLookupModal
        id={'ClsWipAgentLookupModal'}
        lineOfBusiness={'commercial-lines'}
        open={isAgentLookupModalOpen}
        setOpen={setAgentLookupModalOpen}
        setAgentCode={setClWipAgentCodeValue}></AgentLookupModal>
    </>
  );
};

CLSWip.propTypes = {
  navSource: PropTypes.string,
};

export default CLSWip;
